<template>
  <ts-modal
    ref="modal"
    modal-dialog-class="modal-lg"
    :modal-title="modalTitle"
  >
    <template #modal-content>
      <div class="row gx-2">
        <div class="mb-3 col-sm-6">
          <label
            for="question_name"
            class="form-label"
          >
            Name
          </label>
          <input
            id="question_name"
            v-model="questionData.name"
            type="text"
            :class="{'form-control': true, 'is-invalid': nameInvalid}"
          >
          <div
            v-if="nameInvalid"
            class="invalid-feedback"
          >
            {{ nameErrorMessage }}
          </div>
        </div>
        <div class="mb-3 col-sm-6">
          <label
            for="question_name_fr"
            class="form-label"
          >
            Name (Français)
          </label>
          <input
            id="question_name_fr"
            v-model="questionData.nameFr"
            type="text"
            class="form-control"
          >
        </div>
      </div>
      <div class="row gx-2">
        <div class="mb-3 col-auto">
          <label class="form-label">Options</label>
          <div>
            <div class="form-check">
              <input
                id="participantDetailsSection"
                v-model="participantDetailsSectionType"
                type="checkbox"
                class="form-check-input"
              >
              <label
                for="participantDetailsSection"
                class="form-check-label"
              >
                Display participant data in section
              </label>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #modal-footer>
      <button
        v-if="canRestore"
        type="button"
        class="btn btn-sm btn-info restore"
        @click.prevent="restoreQuestion"
      >
        Restore
      </button>
      <button
        v-if="deleteButtonVisible"
        type="button"
        class="btn btn-sm btn-danger me-auto"
        @click.prevent="deleteQuestion"
      >
        Delete
      </button>
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        data-bs-dismiss="modal"
      >
        Close
      </button>
      <button
        type="button"
        class="btn btn-sm btn-primary"
        @click.prevent="updateQuestion"
      >
        Update
      </button>
    </template>
  </ts-modal>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'

import TsModal from './ts-modal.vue'

import { type Question, QuestionType, SurveySectionType } from './survey-types'
import validate from 'validate.js'

defineProps({
  modalTitle: { default: 'Edit section', type: String },
  questionGroups: { default: (): string[] => [], type: Array }
})

const emit = defineEmits<{
  success: [question: Question, parentQuestion: Question | null]
}>()

type ValidationErrors = {
  name?: string[]
} | null | undefined

const modal = useTemplateRef<InstanceType<typeof TsModal>>('modal')

const defaultQuestionData = (): Question => ({
  id: null,
  uid: null,
  typeId: QuestionType.surveySection,
  rm: false,
  name: null,
  nameFr: null,
  description: null,
  descriptionFr: null,
  groupName: null,
  allowSubQuestions: false,
  allowNotes: false,
  isMandatory: false,
  options: {
    displayInOneLine: false,
    ratingMin: null,
    ratingMax: null,
    ratingNotApplicable: false,
    ratingTableChoiceWidth: null,
    ratingDefinitions: [],
    showRatingDefinitions: false,
    choices: [],
    sectionTypeId: SurveySectionType.general,
    notesMandatory: false,
    notesCustomLabel: false,
    notesLabel: '',
    notesLabelFr: '',
    notesMinLength: null,
    uploadCountMax: null,
    textMinLength: null,
    choicesInSingleColumn: false
  },
  subQuestions: []
})

const canDelete = ref(false)
const parentQuestion = ref<Question | null>(null)
const questionData = ref<Question>(defaultQuestionData())
const validationErrors = ref<ValidationErrors>(null)

const nameInvalid = computed((): boolean => {
  return !!(validationErrors.value && validationErrors.value.name && validationErrors.value.name.length > 0)
})

const nameErrorMessage = computed((): string | null => {
  if (nameInvalid.value && validationErrors.value?.name) {
    return validationErrors.value.name[0]
  }
  return null
})

const deleteButtonVisible = computed((): boolean => {
  return canDelete.value && !questionData.value.rm
})

const canRestore = computed((): boolean => {
  return questionData.value.rm
})

const participantDetailsSectionType = computed({
  get (): boolean {
    return questionData.value.options.sectionTypeId == SurveySectionType.participantDetails
  },

  set (value: boolean): void {
    questionData.value.options.sectionTypeId = value ? SurveySectionType.participantDetails : SurveySectionType.general
  }
})

const showModal = (question: Question, newCanDelete: boolean): void => {
  validationErrors.value = null
  const qData = JSON.parse(JSON.stringify(question)) as Question
  questionData.value = qData
  canDelete.value = newCanDelete

  modal.value?.show()
}

const hideModal = (): void => {
  modal.value?.hide()
}

const validateData = (): boolean => {
  const constraints = {
    name: {
      presence: { allowEmpty: false }
    },
  }

  const qData = {
    name: questionData.value.name,
    nameFr: questionData.value.nameFr,
  }

  validationErrors.value = validate(qData, constraints) as ValidationErrors

  return !validationErrors.value
}

const deleteQuestion = (): void => {
  if (confirm('Are you sure?')) {
    questionData.value.rm = true
    emit('success', questionData.value, parentQuestion.value)
  }
}

const restoreQuestion = (): void => {
  if (confirm('Are you sure?')) {
    questionData.value.rm = false
  }
}

const updateQuestion = (): void => {
  if (validateData()) {
    emit('success', questionData.value, parentQuestion.value)
  }
}

defineExpose({
  hideModal,
  showModal,
})
</script>
